<template>
    <div class="min-h-screen bg-gray-100 overflow-x-hidden">
        <AdminNavbar active="emails" />

        <div class="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
            <h1 class="text-base sm:text-xl font-bold text-gray-800 mb-3 sm:mb-6">Emails enviados</h1>

            <div v-if="error" class="text-red-600 text-sm mb-4">{{ error }}</div>

            <!-- Filters -->
            <div class="flex flex-wrap items-end gap-3 mb-6">
                <div>
                    <label class="block text-[11px] uppercase tracking-wide text-gray-400 mb-1">Data</label>
                    <input type="date" v-model="filters.date" class="input input-bordered input-sm text-sm" @change="load" />
                </div>
                <div>
                    <label class="block text-[11px] uppercase tracking-wide text-gray-400 mb-1">Status</label>
                    <select v-model="filters.status" class="select select-bordered select-sm text-sm" @change="load">
                        <option value="">Todos</option>
                        <option value="sent">Enviado</option>
                        <option value="failed">Falhou</option>
                    </select>
                </div>
                <div class="flex-1 min-w-[180px]">
                    <label class="block text-[11px] uppercase tracking-wide text-gray-400 mb-1">Destinatário</label>
                    <input type="text" v-model="filters.search" placeholder="Buscar por email" class="input input-bordered input-sm text-sm w-full" @keyup.enter="load" />
                </div>
                <button class="btn btn-sm btn-primary" @click="load" :disabled="loading">Filtrar</button>
                <button class="btn btn-sm" @click="resetToToday" :disabled="loading">Hoje</button>
            </div>

            <!-- Summary cards -->
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
                <template v-if="loading">
                    <div v-for="i in 3" :key="i" class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 sm:p-5 min-w-0 overflow-hidden">
                        <div class="skeleton h-3 w-24 rounded mb-2"></div>
                        <div class="skeleton h-7 w-16 rounded"></div>
                    </div>
                </template>
                <template v-else>
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 sm:p-5 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Enviados em {{ formatDateLabel(summary.date) }}</div>
                        <div class="text-xl sm:text-2xl font-bold text-emerald-600">{{ summary.sentCount }}</div>
                    </div>
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 sm:p-5 min-w-0 overflow-hidden">
                        <div class="text-xs text-gray-400 mb-1">Falharam</div>
                        <div class="text-xl sm:text-2xl font-bold text-red-500">{{ summary.failedCount }}</div>
                    </div>
                    <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 sm:p-5 min-w-0 overflow-hidden col-span-2 sm:col-span-1">
                        <div class="text-xs text-gray-400 mb-1">Limite diário Mailgun (free)</div>
                        <div class="text-xl sm:text-2xl font-bold" :class="summary.sentCount >= 100 ? 'text-red-500' : 'text-gray-800'">{{ summary.sentCount }} / 100</div>
                    </div>
                </template>
            </div>

            <div v-if="loading" class="space-y-3">
                <div v-for="i in 5" :key="i" class="skeleton h-14 w-full rounded-xl"></div>
            </div>

            <template v-else>
                <div v-if="items.length === 0" class="bg-white border border-gray-100 rounded-xl py-8 text-center text-gray-400 text-sm">
                    Nenhum email registrado para esse filtro.
                </div>

                <!-- Desktop table -->
                <div v-else class="hidden sm:block overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
                    <table class="table table-sm w-full min-w-[800px]">
                        <thead>
                            <tr class="text-[11px] uppercase tracking-wide">
                                <th class="text-left !text-black">Destinatário</th>
                                <th class="text-left !text-black">Assunto</th>
                                <th class="text-center !text-black">Tipo</th>
                                <th class="text-center !text-black">Status</th>
                                <th class="text-center !text-black">Data/Hora</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="log in items" :key="log.uuid" class="hover:bg-gray-50">
                                <td class="text-gray-800">{{ log.recipient }}</td>
                                <td class="text-gray-600">{{ log.subject || '—' }}</td>
                                <td class="text-center">
                                    <span class="text-xs font-mono text-gray-500">{{ log.type || '—' }}</span>
                                </td>
                                <td class="text-center">
                                    <span v-if="log.status === 'sent'" :class="adminBadgeClass('success')">Enviado</span>
                                    <span v-else :class="adminBadgeClass('error')" :title="log.error_message">Falhou</span>
                                </td>
                                <td class="text-center text-xs text-gray-400 whitespace-nowrap">{{ formatDateTime(log.created_at) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Mobile cards -->
                <div v-if="items.length > 0" class="sm:hidden space-y-3">
                    <div v-for="log in items" :key="log.uuid" class="bg-white border border-gray-100 rounded-xl shadow-sm p-4 min-w-0 overflow-hidden">
                        <div class="flex items-center justify-between gap-2 mb-2">
                            <div class="font-medium text-gray-800 truncate">{{ log.recipient }}</div>
                            <span v-if="log.status === 'sent'" :class="adminBadgeClass('success')">Enviado</span>
                            <span v-else :class="adminBadgeClass('error')">Falhou</span>
                        </div>
                        <div class="text-xs text-gray-500 truncate mb-1">{{ log.subject || '—' }}</div>
                        <div class="flex items-center justify-between text-xs text-gray-400">
                            <span class="font-mono">{{ log.type || '—' }}</span>
                            <span>{{ formatDateTime(log.created_at) }}</span>
                        </div>
                    </div>
                </div>

                <div v-if="total > items.length" class="text-center text-xs text-gray-400 mt-4">
                    Mostrando {{ items.length }} de {{ total }}
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import AdminEmailService from '@/services/admin/AdminEmailService';
import { adminBadgeClass } from '@/constants/adminBadge';
import AdminNavbar from '@/components/admin/AdminNavbar.vue';

function todayIsoDate() {
    return new Date().toISOString().slice(0, 10);
}

export default {
    name: 'AdminEmails',
    components: { AdminNavbar },
    setup() {
        const items = ref([]);
        const total = ref(0);
        const summary = ref({ sentCount: 0, failedCount: 0, date: todayIsoDate() });
        const loading = ref(true);
        const error = ref('');
        const filters = reactive({ date: todayIsoDate(), status: '', search: '' });

        async function load() {
            loading.value = true;
            error.value = '';
            try {
                const { data } = await AdminEmailService.getEmailLogs({
                    date: filters.date,
                    status: filters.status || undefined,
                    search: filters.search || undefined
                });
                items.value = data.data.items;
                total.value = data.data.total;
                summary.value = data.data;
            } catch {
                error.value = 'Erro ao carregar log de emails';
            } finally {
                loading.value = false;
            }
        }

        function resetToToday() {
            filters.date = todayIsoDate();
            filters.status = '';
            filters.search = '';
            load();
        }

        function formatDateLabel(dateStr) {
            if (!dateStr) return '';
            const [y, m, d] = dateStr.split('-');
            return `${d}/${m}/${y}`;
        }

        function formatDateTime(value) {
            return new Date(value).toLocaleString('pt-BR');
        }

        onMounted(load);

        return { items, total, summary, loading, error, filters, load, resetToToday, formatDateLabel, formatDateTime, adminBadgeClass };
    }
};
</script>
